import {
  BookOpenIcon,
  EyeIcon,
  HistoryIcon,
  LayoutDashboardIcon,
  type LucideIcon,
  SettingsIcon,
  ShieldCheckIcon,
} from "lucide-react";
import { isFeatureEnabled } from "@/services/feature-flags";

/**
 * Navigation model for the Calm Authority shell. We wire ONLY routes that
 * actually exist (decision: "wire only real routes") — legacy prototype-only
 * Generator / Sygnały and the route-less Team / Billing items stay omitted;
 * team + billing remain reachable from the account menu.
 *
 * `flag` gates an item behind a feature flag (evaluated at render). `match`
 * lists extra pathname prefixes that should light the item as active (e.g. an
 * audit's `/results/:id` keeps the audit entry active).
 *
 * Labels are i18n keys in the `common` namespace, resolved by the caller.
 */

type FeatureFlagName = Parameters<typeof isFeatureEnabled>[0];

export interface NavItemDef {
  /** Route path (also the NavLink target). */
  to: string;
  /** i18n key, e.g. `nav.upload`, resolved against the `common` namespace. */
  labelKey: string;
  icon: LucideIcon;
  /** Optional feature flag; the item is hidden when the flag is off. */
  flag?: FeatureFlagName;
  /** Extra pathname prefixes that also mark this item active. */
  match?: string[];
}

export interface NavGroupDef {
  /** i18n key for the group heading. */
  labelKey: string;
  items: NavItemDef[];
}

const NAV_GROUPS: NavGroupDef[] = [
  {
    labelKey: "shell.groupWork",
    items: [
      { to: "/home", labelKey: "nav.home", icon: LayoutDashboardIcon },
      { to: "/", labelKey: "nav.upload", icon: ShieldCheckIcon, match: ["/results"] },
      {
        to: "/monitoring",
        labelKey: "nav.monitoring",
        icon: EyeIcon,
        flag: "WEBSITE_MONITORING",
        match: ["/monitoring"],
      },
      {
        to: "/knowledge-base",
        labelKey: "nav.kb",
        icon: BookOpenIcon,
        flag: "KNOWLEDGE_BASE",
      },
    ],
  },
  {
    labelKey: "shell.groupActivity",
    items: [{ to: "/history", labelKey: "nav.history", icon: HistoryIcon }],
  },
  {
    labelKey: "shell.groupManage",
    items: [{ to: "/settings", labelKey: "nav.settings", icon: SettingsIcon }],
  },
];

/** Nav groups with feature-flagged items filtered out. */
export function getNavGroups(): NavGroupDef[] {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.flag || isFeatureEnabled(item.flag)),
  })).filter((group) => group.items.length > 0);
}

/** True when `pathname` should render `item` as the active nav entry. */
export function isNavItemActive(item: NavItemDef, pathname: string): boolean {
  if (item.to === "/") {
    if (pathname === "/") {
      return true;
    }
  } else if (pathname === item.to || pathname.startsWith(`${item.to}/`)) {
    return true;
  }
  return (item.match ?? []).some((prefix) => pathname.startsWith(prefix));
}

/**
 * The i18n label key for the current route's breadcrumb title, or null when the
 * route has no shell title (e.g. auth screens that render outside the shell).
 */
export function routeTitleKey(pathname: string): string | null {
  if (pathname.startsWith("/results")) {
    return "nav.upload";
  }
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (isNavItemActive(item, pathname)) {
        return item.labelKey;
      }
    }
  }
  return null;
}
